import { faCircleInfo } from "@fortawesome/pro-solid-svg-icons/faCircleInfo";
import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons/faTriangleExclamation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, Box, CardContent, FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, TextField, Typography } from "@mui/material";
import { AppImageDimension, AppImages } from "app/appImages";
import { MprcCard, MprcHr, MprcIconButton } from "components";
import { IconButtonCopy } from "components/iconButtons/IconButtonCopy";
import { AutoCompleteDataModel } from "components/MprcAutoComplete";
import { MprcContainer } from "components/MprcContainer";
import { MprcDatePicker } from "components/MprcDatePicker";
import { MprcImageSelection } from "components/MprcImageHandler";
import { MprcInformationDialog } from "components/MprcInformationDialog";
import { MprcStringToHtml } from "components/MprcStringToHtml";
import { MprcSwitch } from "components/MprcSwitch";
import { BlockReasonSelectorResponse, CustomFieldValueDto, FileGetDto, ManagedUserQueryResponse, ManagedUserQuerySettings, UserDto, UserStatus, UserType, WorkplaceDto } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import HtmlUtils from "lib/HtmlUtils";
import "lib/JsTypes.extensions";
import MediaUtils from "lib/MediaUtils";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useSession } from "providers/SessionProvider";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomFieldInput, getCustomFieldGridSize } from "views/foundation/customField/components/CustomFieldInput";
import { useEditUserContext } from "views/foundation/user/Home/EditUser/EditUser";
import useCheckPassword from "views/foundation/user/hooks/useCheckPassword";
import { WorkplaceAutoComplete } from "views/foundation/workplace/components/WorkplaceAutoComplete";
////////////////////////////////////////////////////////////////////////////

interface IValidationFields {
  displayName: boolean;
  name: boolean;
  preferredLocale: boolean;
  password: boolean;
  customFields: { [key: string]: boolean; };
  email: boolean;
  thumbnail?: boolean;
  workplace?: boolean;
  workplaceText?: boolean;
  accessExpiresOn: boolean;
  blockReason: boolean;
}

interface IPostValidation {
  name: string[] | undefined;
  displayName: string[] | undefined;
  email: string[] | undefined;
  generatePassword: string[] | undefined;
  password: string[] | undefined;
  workplace: string[] | undefined;
  workplaceText: string[] | undefined;
  thumbnail: string[] | undefined;
  preferredLocale: string[] | undefined;
  blockReason: string[] | undefined;
  accessExpiresOn: string[] | undefined;
  unblockAt: string[] | undefined;
  customFields: { [key: string]: string[] | undefined; };
}

interface IUserMainBoxData {
  userId: string;
  name: string;
  displayName: string;
  email: string | null;
  preferredLocale: string | null;
  status: UserStatus;
  workplace: WorkplaceDto | null;
  workplaceText: string | null;
  thumbnail: FileGetDto | null;
  isExternalUser: boolean;
  accessExpiresOn: Date | null;
  blockReasonId: string | null;
  unblockAt: Date | null;
  blocker: UserDto | null;
  blockedOn: Date | null;
  settings: ManagedUserQuerySettings;
  hasSamlAccount: boolean;
  generatePassword: boolean;
  password: string | null;
  userType: UserType;
  customFieldsBasic: CustomFieldValueDto[] | null;
}

interface IBlockProps {
  blockedOn: Date | null;
  blockReasonId: string | null;
  blocker: UserDto | null;
}
interface IProps {
  viewData: IUserMainBoxData;
  initialBlockProps: IBlockProps | null;
  loadDto: ManagedUserQueryResponse | null;
  onFormValidation: (valid: boolean) => void;
}

function getDefaultValidate(
  currentValidation: IValidationFields,
  dto: IUserMainBoxData,
  isNewUser: boolean,
  passwordPassed: boolean,
  settings: ManagedUserQuerySettings,
  ignoreCustomFields: boolean
): IValidationFields {
  var obj = {
    ...currentValidation,
    name: dto.name != undefined && dto.name.length > 0,
    email: (dto.email != undefined && dto.email.length > 0) || !settings.isEmailRequired,
    workplace: !settings.workplaceRequiredForNewUsers || settings.isWorkplaceFreeText || (dto.workplace?.workplaceId != undefined),
    workplaceText: !settings.workplaceRequiredForNewUsers || !settings.isWorkplaceFreeText || (dto.workplaceText != undefined && dto.workplaceText.length > 0),
    displayName: dto.displayName != undefined && dto.displayName.length > 0,
    preferredLocale: dto.preferredLocale != undefined && dto.preferredLocale.length > 0,
    password: !isNewUser || dto.generatePassword === true || passwordPassed,
    accessExpiresOn: dto.accessExpiresOn == null || moment(dto.accessExpiresOn).isValid(),
    blockReason: dto.status == UserStatus.Active || dto.status == UserStatus.Deleted || dto.blockReasonId != null
  } as IValidationFields;

  if (!ignoreCustomFields) {
    dto.customFieldsBasic
      ?.filter((x) => x.isRequired)
      .forEach((f) => (obj.customFields[f.fieldId] = f.fieldValue != undefined && f.fieldValue.length > 0));
  }

  return obj;
}

////////////////////////////////////////////////////////////////////////////

/**
 * Edição de Usuário - Formulário principal
 * @param props
 */
export const EditUserMainBox: React.FC<IProps> = (props) => {

  const { initialBlockProps, loadDto, viewData, onFormValidation } = props;
  const { isNewUser, blockReasons, readonly, onFormUpdate, onDialog, viewErrors, onError } = useEditUserContext();

  const session = useSession();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const filteredBlockReasons = useRef<BlockReasonSelectorResponse[]>([]);
  const hasActiveWithLoginBlocked = useRef(false);
  const hasInactiveBlocked = useRef(false);

  const { checkPassword } = useCheckPassword(viewData.settings, viewData.name ?? "");
  const [formValidation, setFormValidation] = useState<IValidationFields>(
    getDefaultValidate({ customFields: {} } as IValidationFields, viewData, isNewUser, false, viewData.settings, false)
  );

  const vErrors = viewErrors ?? {};

  const postValidation: IPostValidation = {
    name: vErrors["name"],
    displayName: vErrors["displayName"],
    email: vErrors["email"],
    generatePassword: vErrors["generatePassword"],
    password: vErrors["password"],
    workplace: vErrors["workplaceId"],
    workplaceText: vErrors["workplaceText"],
    thumbnail: vErrors["thumbnailId"],
    preferredLocale: vErrors["preferredLocale"],
    blockReason: vErrors["blockReasonId"],
    accessExpiresOn: vErrors["accessExpiresOn"],
    unblockAt: vErrors["unblockAt"],
    customFields: {}
  };

  viewData.customFieldsBasic?.map((f) => (postValidation.customFields[f.fieldId] = vErrors[`customFields.${f.fieldId}`] ?? undefined));

  ////////////////////////////////////////////////////////////////////////////
  ///

  function handleUpdateRecord(name: keyof ManagedUserQueryResponse, v: any) {

    var newViewData = { ...viewData, [name]: v };

    //Se status mudar, ajusta blockReasonId se necessário
    if (name == "status") {
      var blockReasonId: string | null = null;
      filteredBlockReasons.current = blockReasons?.filter(p => p.appliesToUserStatus == v);
      if (filteredBlockReasons.current.length > 0) {
        if (filteredBlockReasons.current.find(p => p.blockReasonId == initialBlockProps?.blockReasonId) == null)
          blockReasonId = filteredBlockReasons.current[0].blockReasonId;
        else if (viewData.blockReasonId != initialBlockProps?.blockReasonId)
          blockReasonId = initialBlockProps?.blockReasonId ?? null;
      }

      newViewData = { ...newViewData, blockReasonId };
    }

    setFormValidation((state) =>
      getDefaultValidate(state, newViewData, isNewUser, checkPassword(name == "password" ?
        v : viewData.password ?? "").length == 0, viewData.settings, true)
    );

    onFormUpdate(newViewData);
  }

  ///
  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement> | SelectChangeEvent) => {
    const inputName = event.target.name as keyof ManagedUserQueryResponse;
    handleUpdateRecord(inputName, event.target.value);
  };

  ///
  const handleUpdateCustomField = (fieldId: string, value: any) => {
    let newCustomFields = viewData.customFieldsBasic?.map((item) => {
      if (item.fieldId === fieldId) item.fieldValue = value;
      return item;
    });

    handleUpdateRecord("customFieldsBasic", newCustomFields);
  };

  ///
  const setSamlInfoDialog = () => {
    onDialog(
      <MprcInformationDialog
        message={
          <>
            <p>{t("User.Edit.SamlAccountInfoMessageL1")}</p>
            <ul>
              <li>{t("User.Edit.SamlAccountInfoMessageL2")}</li>
              <li>{t("User.Edit.SamlAccountInfoMessageL3")}</li>
            </ul>
          </>
        }
        onClose={() => onDialog(undefined)}
      />
    );
  };

  ///
  const copyToClipboard = () => {
    if (viewData.password != undefined) {
      HtmlUtils.copyToClipboard(viewData.password)
        .then(() =>
          enqueueSnackbar(t("User.Common.PasswordCopiedSuccessMessage"), {
            variant: "success"
          })
        )
        .catch(() =>
          enqueueSnackbar(t("User.Common.PasswordCopiedErrorMessage"), {
            variant: "error"
          })
        );
    }
  };

  ///
  useEffect(() => {
    hasActiveWithLoginBlocked.current = blockReasons.filter(p => p.appliesToUserStatus == UserStatus.ActiveWithLoginBlocked && p.isReserved !== true).length > 0;
    hasInactiveBlocked.current = blockReasons.filter(p => p.appliesToUserStatus == UserStatus.Inactive && p.isReserved !== true).length > 0;
  }, [blockReasons]);

  ////////////////////////////////////////////////////////////////////////////
  ///
  useEffect(() => {
    onFormValidation(
      Object.values(formValidation).filter((x) => x === false).length == 0 &&
      Object.entries(formValidation.customFields).filter(([key, v]) => v === false).length == 0
    );
  }, [formValidation]);


  ////////////////////////////////////////////////////////////////////////////
  /// RENDER
  ////////////////////////////////////////////////////////////////////////////

  const localPassswordValidation = !isNewUser || viewData.generatePassword ? [] : checkPassword(viewData.password ?? "");

  return (
    <div>
      <MprcContainer>
        <MprcCard>
          <CardContent>
            {viewData.hasSamlAccount && (
              <MprcContainer>
                <Alert
                  icon={<FontAwesomeIcon icon={faTriangleExclamation} />}
                  severity="warning"
                  action={
                    <MprcIconButton color="cancel" size="small" onClick={setSamlInfoDialog}>
                      <FontAwesomeIcon icon={faCircleInfo} />
                    </MprcIconButton>
                  }
                >
                  {t("User.Edit.UserHasSamlAccountMessage")}
                </Alert>
              </MprcContainer>
            )}
            <Grid container spacing={2}>
              <Grid alignSelf="flex-start" justifySelf="center">
                <MprcImageSelection
                  variant="circular"
                  allowEdit={!readonly}
                  allowSelect={!readonly}
                  enableClear={!readonly}
                  initialSource="local"
                  backgroundType="solid"
                  emptyImageUrl={AppImages.UserThumbnail}
                  currentImageUrl={viewData.thumbnail?.url}
                  acceptedFiles={MediaUtils.DropzoneAcceptImages()}
                  imageDimensions={AppImageDimension.SquarePicture}
                  onClear={() => {
                    handleUpdateRecord("thumbnail", null);
                  }}
                  onFileUploadSuccess={(data) => {
                    handleUpdateRecord("thumbnail", data.thumbnail ? data.thumbnail : data.mainFile);
                    return true;
                  }}
                  onFileUploadStarted={() => setFormValidation({ ...formValidation, thumbnail: false })}
                  onFileUploadFinished={() => setFormValidation({ ...formValidation, thumbnail: true })}
                />
                <FormHelperText error={postValidation?.thumbnail != undefined}>
                  <MprcStringToHtml text={postValidation?.thumbnail} color="error" />
                </FormHelperText>
              </Grid>
              <Grid size="grow">
                <TextField
                  fullWidth
                  required
                  autoFocus
                  name="name"
                  label={t("User.Edit.FullNameLabel")}
                  error={!formValidation.name || postValidation?.name != undefined}
                  helperText={postValidation?.name && <MprcStringToHtml text={postValidation?.name} color="error" />}
                  margin="dense"
                  value={viewData.name || ""}
                  onChange={(e) => handleChange(e)}
                  slotProps={{
                    input: {
                      inputProps: { maxLength: 255 }
                    }
                  }}
                  disabled={viewData.userType == UserType.Admin || readonly}
                />
                <TextField
                  fullWidth
                  required
                  name="displayName"
                  label={t("User.Edit.DisplayNameLabel")}
                  error={!formValidation.displayName || postValidation?.displayName != undefined}
                  helperText={
                    postValidation?.displayName && (
                      <MprcStringToHtml text={postValidation?.displayName} color="error" />
                    )
                  }
                  margin="dense"
                  value={viewData.displayName || ""}
                  onChange={(e) => handleChange(e)}
                  slotProps={{
                    input: {
                      inputProps: { maxLength: 255 }
                    }
                  }}
                  disabled={viewData.userType == UserType.Admin || readonly}
                />
                <TextField
                  fullWidth
                  required={viewData.settings.isEmailRequired}
                  name="email"
                  label={t("User.Edit.EmailLabel")}
                  disabled={viewData.userType == UserType.Admin || viewData.hasSamlAccount || readonly}
                  error={!formValidation.email || postValidation?.email != undefined}
                  helperText={
                    postValidation?.email && <MprcStringToHtml text={postValidation?.email} color="error" />
                  }
                  margin="dense"
                  value={viewData.email || ""}
                  onChange={(e) => handleChange(e)}
                  slotProps={{
                    input: {
                      inputProps: { maxLength: 2048 }
                    }
                  }}
                />
                <Grid container columnSpacing={4}>
                  {viewData.customFieldsBasic?.map((item, i) => (
                    <Grid key={i} size={{ xs: getCustomFieldGridSize(item.showWidth) }}>
                      <CustomFieldInput
                        margin="dense"
                        field={item}
                        disabled={readonly}
                        useDefaultValue={isNewUser}
                        onChange={(value, formattedValue, isValid) => {
                          handleUpdateCustomField(item.fieldId, value);

                          setFormValidation({
                            ...formValidation,
                            customFields: { ...formValidation.customFields, [item.fieldId]: isValid }
                          });
                        }}
                        error={postValidation.customFields[item.fieldId]}
                        validateOnFirstMount={true}
                      />
                    </Grid>
                  ))}
                </Grid>

                <Grid container spacing={4} alignItems="flex-end">
                  <Grid size={{ xs: 6 }}>
                    <FormControl
                      required
                      fullWidth
                      error={!formValidation.preferredLocale}
                      sx={{ mb: "4px", mt: "8px" }}
                    >
                      <InputLabel>{t("User.Edit.LocaleLabel")}</InputLabel>
                      <Select
                        error={postValidation?.preferredLocale != undefined}
                        name="preferredLocale"
                        value={viewData.preferredLocale || ""}
                        label={t("User.Edit.LocaleLabel")}
                        onChange={(e) => handleChange(e)}
                        disabled={readonly}
                      >
                        {session.config.inputLocales.map((l) => (
                          <MenuItem key={l.locale} value={l.locale}>
                            {l.nameR.find(x => x.locale == session.uiCulture)?.text ?? l.name}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText error={postValidation?.preferredLocale != undefined}>
                        <MprcStringToHtml text={postValidation?.preferredLocale} color="error" />
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                </Grid>
                {viewData.settings.isWorkplaceFreeText === true && (
                  <TextField
                    fullWidth
                    name="workplaceText"
                    required={viewData.settings.workplaceRequiredForNewUsers}
                    label={viewData.settings.workplaceLabel}
                    margin="dense"
                    disabled={readonly}
                    value={viewData.workplaceText || ""}
                    onChange={(e) => handleChange(e)}
                    slotProps={{
                      input: {
                        inputProps: { maxLength: 255 }
                      }
                    }}
                    error={!formValidation.workplaceText || postValidation?.workplaceText != undefined}
                    helperText={<MprcStringToHtml text={postValidation?.workplaceText} color="error" />}
                  />
                )}
                {viewData.settings.isWorkplaceFreeText === false &&
                  <WorkplaceAutoComplete
                    label={viewData.settings.workplaceLabel}
                    required={viewData.settings.workplaceRequiredForNewUsers}
                    multiSelect={false}
                    onError={onError}
                    disabled={readonly}
                    selectedItems={
                      viewData.workplace != undefined ?
                        [{ key: viewData.workplace.workplaceId, label: viewData.workplace.name, canDelete: true } as AutoCompleteDataModel] :
                        []
                    }
                    onChange={(items) => {
                      if (items.length > 0) handleUpdateRecord("workplace", { workplaceId: items[0].key, name: items[0].label });
                      else handleUpdateRecord("workplace", null);
                    }}
                    error={!formValidation.workplace || postValidation?.workplace != undefined}
                    helperText={<MprcStringToHtml text={postValidation?.workplace} color="error" />}
                  />
                }

                <MprcSwitch
                  margin="dense"
                  label={t("User.Edit.ExternalUserLabel")}
                  checked={viewData.isExternalUser!}
                  disabled={viewData.userType == UserType.Admin || readonly}
                  onChange={() => {
                    handleUpdateRecord("isExternalUser", !viewData.isExternalUser);
                  }}
                  color="primary"
                />
                {
                  !isNewUser &&
                  <section id="status">
                    <Grid container rowSpacing={2} columnSpacing={4} alignItems="center">
                      <Grid size={{ xs: 12, md: 6 }}>
                        {viewData.status != UserStatus.Deleted &&
                          <FormControl fullWidth margin="dense">
                            <InputLabel>{t("User.Edit.StatusLabel")}</InputLabel>
                            <Select
                              disabled={viewData.userType == UserType.Admin || readonly}
                              value={viewData.status}
                              label={t("User.Edit.StatusLabel")}
                              onChange={(e) => handleUpdateRecord("status", e.target.value)}
                              error={postValidation?.blockReason != undefined}
                            >
                              <MenuItem value={UserStatus.Active}>
                                <Typography variant="h6" color="success.main" sx={{ textTransform: "uppercase" }}>{t("Enums.UserStatus.Active")}</Typography>
                              </MenuItem>
                              {
                                hasActiveWithLoginBlocked.current &&
                                <MenuItem value={UserStatus.ActiveWithLoginBlocked}>
                                  <Typography variant="h6" color="warning.main" sx={{ textTransform: "uppercase" }}>{t("Enums.UserStatus.ActiveWithLoginBlocked")}</Typography>
                                </MenuItem>
                              }
                              {
                                hasInactiveBlocked.current &&
                                <MenuItem value={UserStatus.Inactive}>
                                  <Typography variant="h6" color="error.main" sx={{ textTransform: "uppercase" }}>{t("Enums.UserStatus.Inactive")}</Typography>
                                </MenuItem>
                              }
                            </Select>
                            <FormHelperText error={postValidation?.blockReason != undefined}>
                              <MprcStringToHtml text={postValidation?.blockReason} color="error" />
                            </FormHelperText>
                          </FormControl>
                        }
                      </Grid>
                      {
                        viewData.status == UserStatus.Active
                          ? <Grid size={{ xs: 12, md: 6 }}>
                            <MprcDatePicker
                              disablePast
                              fullWidth
                              label={t("User.Edit.AccessExpiresOnLabel")}
                              value={viewData.accessExpiresOn == null ? null : viewData.accessExpiresOn}
                              disabled={viewData.userType == UserType.Admin || readonly}
                              onChange={(date) => handleUpdateRecord("accessExpiresOn", date)}
                              helperText={<MprcStringToHtml text={postValidation?.accessExpiresOn} color="error" />}
                              error={postValidation?.accessExpiresOn != undefined}
                            />
                          </Grid>
                          : <span>&nbsp;</span>
                      }
                      {
                        viewData.status != UserStatus.Active && viewData.status != UserStatus.Deleted &&
                        <>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth>
                              <InputLabel>{t("User.Edit.BlockReasonLabel")}</InputLabel>
                              <Select
                                value={viewData.blockReasonId ?? 0}
                                label={t("User.Edit.BlockReasonLabel")}
                                onChange={(e) => handleUpdateRecord("blockReasonId", e.target.value == 0 ? null : e.target.value)}
                                error={!formValidation.blockReason || postValidation?.blockReason != undefined}
                                disabled={viewData.userType == UserType.Admin || readonly}
                              >
                                {
                                  filteredBlockReasons.current.map((x) => (
                                    <MenuItem key={x.blockReasonId} value={x.blockReasonId}>
                                      {x.name}
                                    </MenuItem>
                                  ))
                                }
                              </Select>
                              <FormHelperText error={postValidation?.blockReason != undefined}>
                                <MprcStringToHtml text={postValidation?.blockReason} color="error" />
                              </FormHelperText>
                            </FormControl>
                          </Grid>
                          {
                            viewData.status == UserStatus.ActiveWithLoginBlocked &&
                            <Grid size={{ xs: 12, md: 6 }}>
                              <MprcDatePicker
                                disablePast
                                fullWidth
                                label={t("User.Edit.UnblockAtLabel")}
                                value={viewData.unblockAt == null ? null : viewData.unblockAt}
                                disabled={viewData.userType == UserType.Admin || readonly}
                                onChange={(date) => handleUpdateRecord("unblockAt", date)}
                                error={postValidation?.unblockAt != undefined}
                                helperText={<MprcStringToHtml text={postValidation?.unblockAt} color="error" />}
                              />
                            </Grid>
                          }
                        </>
                      }
                    </Grid>
                    {
                      initialBlockProps?.blockedOn != null && viewData.blockReasonId == initialBlockProps?.blockReasonId &&
                      <Box sx={{ mt: 1 }}>
                        <Typography
                          color={viewData.status == UserStatus.ActiveWithLoginBlocked ? "warning.main" : "error.main"}
                        >
                          {t(viewData.status == UserStatus.Deleted
                            ? "User.Edit.DeletedMessage"
                            : "User.Edit.BlockedMessage",
                            { date: Formaters.performaDate(viewData.blockedOn), name: viewData.blocker?.name ?? "" })}
                        </Typography>
                      </Box>
                    }

                  </section>
                }
                {isNewUser && (
                  <section id="password">
                    <MprcHr />
                    <FormControl fullWidth>
                      <FormHelperText error={postValidation["generatePassword"] != undefined} sx={{ mx: 0 }}>
                        {postValidation["generatePassword"]}
                      </FormHelperText>
                      <MprcSwitch
                        label={t("User.Edit.GeneratePwdLabel")}
                        checked={viewData.generatePassword}
                        onChange={() => {
                          handleUpdateRecord("generatePassword", !viewData.generatePassword);
                        }}
                        color="primary"
                      />
                    </FormControl>
                    {
                      !viewData.generatePassword && (
                        <>
                          <TextField
                            label={t("User.Edit.PasswordLabel")}
                            margin="dense"
                            fullWidth
                            required
                            error={
                              localPassswordValidation.length > 0 || !formValidation.password || postValidation?.password != undefined
                            }
                            name="password"
                            autoComplete="off"
                            placeholder={t("User.Edit.PasswordPlaceholder")}
                            helperText={
                              postValidation?.password != undefined
                                ? <MprcStringToHtml text={postValidation?.password} color="error" />
                                : localPassswordValidation.length > 0
                                  ? <MprcStringToHtml text={localPassswordValidation} />
                                  : <span>{loadDto && loadDto.generatePassword && loadDto.password == viewData.password ? t("User.Edit.PasswordHint") : ""}</span>
                            }
                            value={viewData.password}
                            onChange={(e) => handleChange(e)}
                            slotProps={{
                              input: {
                                inputProps: { maxLength: 64 },
                                endAdornment: (
                                  <IconButtonCopy
                                    size="small"
                                    onClick={copyToClipboard}
                                    disabled={viewData.password == undefined || viewData.password.length === 0}
                                  />
                                )
                              }
                            }}
                          />
                        </>
                      )
                    }
                  </section>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </MprcCard>
      </MprcContainer>
    </div>
  );
};
